/* ============================================================================
   Typed fetch wrapper for the UVA Creative Requests API.
   - Attaches the JWT bearer token from localStorage on every call.
   - Sends/receives JSON.
   - Normalizes every failure into ApiError { status, message }.

   Every module MUST call the API through apiGet / apiPost / apiPut here.
   Do not hand-roll fetch() in feature code.
   ============================================================================ */

export const TOKEN_KEY = 'uva.token';

/**
 * Base origin for the API. Empty in local dev (Vite proxies /api -> :7071).
 * In production it is set at build time (VITE_API_BASE) to the standalone
 * Azure Functions app origin, so relative "/api/..." paths become absolute.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export interface ApiError {
  status: number;
  message: string;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
}

function buildHeaders(hasBody: boolean): Headers {
  const headers = new Headers();
  if (hasBody) headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as Record<string, unknown>).message)
        : typeof body === 'string' && body
          ? body
          : res.statusText) || 'Request failed';
    const err: ApiError = { status: res.status, message };
    throw err;
  }

  return body as T;
}

async function request<T>(method: string, path: string, payload?: unknown): Promise<T> {
  const hasBody = payload !== undefined;
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers: buildHeaders(hasBody),
      body: hasBody ? JSON.stringify(payload) : undefined,
    });
  } catch {
    const err: ApiError = { status: 0, message: 'Network error — could not reach the server.' };
    throw err;
  }
  return handle<T>(res);
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body ?? {});
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body ?? {});
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

export function isApiError(e: unknown): e is ApiError {
  return !!e && typeof e === 'object' && 'status' in e && 'message' in e;
}
